// useAreaOpsQueue — the heart of the "Alan İşlemleri" pin panel.
//
// Owns, for a single image box, the ordered list of pins and runs them as a
// CHAIN: each running pin takes `currentUrl` (the previous pin's result, or the
// original for the first) as input, so results never clobber each other. Pins
// run one at a time (the chain is inherently sequential) but the user is never
// blocked — they keep editing while the pipeline advances.
//
// Version model (NON-destructive history):
//   - Each completed pin is a version; the "history list" in the UI is
//     Original + every done pin's resultUrl.
//   - `currentUrl` is a pointer to the version currently shown/applied.
//   - revertToPin / revertToOriginal only MOVE that pointer + re-apply — they
//     never delete pins, so going back to an older version keeps all the newer
//     ones reachable. New ops chain off whatever currentUrl points at.
//
// Persistence: pins + currentUrl are mirrored to
// localStorage["area-ops-queue-<boxId>"] so closing/reopening the panel keeps
// the list, the current version, and any in-flight job (resumed by its jobId).
//
// Batch ("Toplu Alan İşlemleri"): "Listeye Ekle" parks a draft as `staged`
// (never picked by the drain) — a box on the image that reopens its popup when
// clicked. applyStaged sends 1 staged op the legacy way and 2+ as ONE batch pin
// (featureId "area-ops-batch") — one job, one version. The member pins leave
// the list; their snapshots live in `batchOps`, in staged order.

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { getAreaOpFeature, isAreaOpReady, runAreaOp, pollAreaOpJob } from "./areaOpsSubmit";
import {
  AREA_OPS_BATCH_OPERATION,
  MAX_BATCH_OPS,
  isAreaOpsBatchEnabled,
  canStageAreaOp,
  runAreaOpsBatch,
  pollBatchJob,
  mergeBatchOpStatuses,
} from "./areaOpsBatch";
import { notifyInsufficientBalance } from "../../../utils/insufficientBalance";
import { approveJob } from "../../../../common/server_utils.es6";
import { onJobsSettled, SETTLED_APPROVED, SETTLED_DECLINED } from "../../../utils/jobSettled";

const storageKey = (boxId) => `area-ops-queue-${boxId}`;

// Only these fields are persisted (no functions / transient progress noise).
function serializePins(pins) {
  return pins.map((p) => ({
    id: p.id,
    num: p.num,
    featureId: p.featureId,
    status: p.status,
    region: p.region || null,
    point: p.point || null,
    whole: !!p.whole,
    config: p.config || {},
    jobId: p.jobId || null,
    resultUrl: p.resultUrl || null,
    error: p.error || null,
    batchOps: p.batchOps || null,
    steps: p.steps || null,
    partial: !!p.partial,
    settled: p.settled || null,
  }));
}

// İş tepsisinde kapanan (onaylanan / reddedilen) işin pin'ine kapanışı yaz.
// Yalnız henüz sonuçlanmamış (running/queued) pin'ler: done pin zaten
// uygulanmış bir sürümdür. Değişiklik yoksa aynı diziyi döndürür.
export function markSettledPins(pins, jobIds, outcomes = {}) {
  let changed = false;
  const next = pins.map((p) => {
    if (!p || !p.jobId || p.settled || !jobIds.includes(p.jobId)) return p;
    if (p.status !== "running" && p.status !== "queued") return p;
    changed = true;
    return { ...p, settled: outcomes[p.jobId] || SETTLED_DECLINED };
  });
  return changed ? next : pins;
}

// Panel KAPALIYKEN (hook unmount) tepsi kapanışını localStorage'daki
// kuyruklara işler — JobWidgetHost çağırır. Yoksa panel tekrar açılınca
// drain, reddedilmiş işi uygular / onaylanmışı ikinci kez uygular.
export function settleAreaOpsStorage(jobIds, outcomes = {}, storage) {
  const store = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  if (!store || !jobIds?.length) return;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) keys.push(store.key(i));
    keys.forEach((key) => {
      if (!key || !key.startsWith("area-ops-queue-")) return;
      let parsed = null;
      try {
        parsed = JSON.parse(store.getItem(key));
      } catch {
        return;
      }
      if (!parsed || !Array.isArray(parsed.pins)) return;
      const pins = markSettledPins(parsed.pins, jobIds, outcomes);
      if (pins !== parsed.pins) store.setItem(key, JSON.stringify({ ...parsed, pins }));
    });
  } catch {
    /* non-fatal */
  }
}

const DECLINED_ERROR = "İş tepsisinden reddedildi";

// Resolves once `url` has downloaded and decoded (or failed, or timed out). The
// center <img> keeps painting the previous version until a new src is ready —
// and a sequential batch's step thumbnails download alongside it — so a result
// switched in before that read as "nothing changed". Never rejects: a slow or
// broken image must not hold the result back. No decode() (jsdom) → at once.
export function preloadImage(url, timeoutMs = 30000) {
  if (!url || typeof Image === "undefined") return Promise.resolve();
  const img = new Image();
  if (typeof img.decode !== "function") return Promise.resolve();
  img.src = url;
  let timer;
  return Promise.race([
    img.decode().catch(() => {}),
    new Promise((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
    }),
  ]).finally(() => clearTimeout(timer));
}

// A pin persisted as "running" but with no jobId was interrupted (tab
// closed/crashed) DURING the submit POST, before onJobId fired — the resume
// path keys off jobId, so such a pin would never re-run and would silently
// break the chain. Reset it to "queued" so it re-submits cleanly on load.
function normalizePins(pins) {
  return pins.map((p) =>
    p && p.status === "running" && !p.jobId ? { ...p, status: "queued" } : p
  );
}

function loadPersisted(boxId) {
  try {
    const raw = localStorage.getItem(storageKey(boxId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.pins)) return null;
    parsed.pins = normalizePins(parsed.pins);
    return parsed;
  } catch {
    return null;
  }
}

let PIN_SEQ = 0;
function nextPinId() {
  PIN_SEQ += 1;
  // Random suffix so a fresh session (PIN_SEQ resets to 0) can't mint an id that
  // collides with a pin restored from localStorage.
  return `pin-${PIN_SEQ}-${Math.random().toString(36).slice(2, 8)}`;
}

const countStaged = (pins) => pins.filter((p) => p.status === "staged").length;

// A batch op as a stand-alone pin again. Keeps the id/num of the pin it was
// staged from unless `patch` gives fresh ones.
function opAsPin(op, patch) {
  return {
    id: op.opId,
    num: op.num,
    featureId: op.featureId,
    status: "staged",
    region: op.region || null,
    point: null,
    whole: !!op.whole,
    config: op.config || {},
    jobId: null,
    resultUrl: null,
    error: null,
    progress: 0,
    ...patch,
  };
}

// The error each op of a failed batch carries back. A 400 AREA_OPS_INVALID
// names the ops at fault (errors[{ opId?, message }]): a message goes on the op
// it names, or on every op when it names none. Otherwise every op gets `message`.
function batchOpErrors(ops, message, errors) {
  const named = ops.map((op) =>
    (errors || [])
      .filter((e) => !e.opId || e.opId === op.opId)
      .map((e) => e.message)
      .join("; ")
  );
  return named.some(Boolean) ? named.map((m) => m || null) : ops.map(() => message);
}

export default function useAreaOpsQueue({
  boxId,
  originalUrl,
  ctx,
  onApply,
  onInsufficientBalance,
}) {
  const persisted = boxId ? loadPersisted(boxId) : null;

  // The TRUE original — remembered from the first session. On reopen the box's
  // url is the LAST applied result, so we must NOT re-capture it as "original"
  // (that broke the version list + current-version matching). Persisted value
  // wins; the incoming prop only seeds it the very first time.
  const original = (persisted && persisted.originalUrl) || originalUrl;

  const [pins, setPins] = useState(() => (persisted?.pins ? persisted.pins : []));
  // The image currently shown/applied. On open this MUST reflect what's actually
  // on the canvas right now (the box's current url = the `originalUrl` prop),
  // NOT a stale persisted pointer — otherwise the modal shows a different version
  // than the canvas. The version whose resultUrl matches it gets the ✓.
  // Navigation then moves this pointer NON-destructively (older versions stay).
  const [currentUrl, setCurrentUrl] = useState(() => originalUrl || original);
  // Off for the session once the backend 404s a batch (no batch route).
  const [batchAvailable, setBatchAvailable] = useState(() => isAreaOpsBatchEnabled());
  // Server payload of a batch the balance bounced, until it is reported.
  const [balanceBounce, setBalanceBounce] = useState(null);

  // Mirrors for reads inside async runners (avoid stale closures).
  const pinsRef = useRef(pins);
  const currentUrlRef = useRef(currentUrl);
  const onApplyRef = useRef(onApply);
  const onInsufficientBalanceRef = useRef(onInsufficientBalance);
  const runningRef = useRef(false);
  const abortRef = useRef(null); // AbortController of the pin currently running
  const numRef = useRef(1);

  useEffect(() => {
    pinsRef.current = pins;
  }, [pins]);
  useEffect(() => {
    currentUrlRef.current = currentUrl;
  }, [currentUrl]);
  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);
  useEffect(() => {
    onInsufficientBalanceRef.current = onInsufficientBalance;
  }, [onInsufficientBalance]);

  // Seed the numbering counter past any restored pin.
  useEffect(() => {
    const maxNum = pins.reduce((m, p) => Math.max(m, p.num || 0), 0);
    numRef.current = maxNum + 1;
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every change.
  useEffect(() => {
    if (!boxId) return;
    try {
      localStorage.setItem(
        storageKey(boxId),
        JSON.stringify({ pins: serializePins(pins), currentUrl, originalUrl: original })
      );
    } catch {
      /* quota / serialization — non-fatal */
    }
  }, [boxId, pins, currentUrl, original]);

  // Report a balance bounce from an effect, i.e. once the restaged ops are
  // rendered and saved: the handler may close the panel (see AreaOpsModal).
  useEffect(() => {
    if (!balanceBounce) return;
    setBalanceBounce(null);
    (onInsufficientBalanceRef.current || notifyInsufficientBalance)(balanceBounce);
  }, [balanceBounce]);

  const patchPin = useCallback((id, patch) => {
    setPins((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  // Panel açıkken tepsi kapanışı: pin'e işle (pinsRef de hemen — çalışan
  // executePin sonucu alınca oradan okur). Kapalıyken JobWidgetHost
  // settleAreaOpsStorage ile localStorage'a yazar.
  useEffect(
    () =>
      onJobsSettled((jobIds, outcomes) => {
        const next = markSettledPins(pinsRef.current, jobIds, outcomes);
        if (next === pinsRef.current) return;
        pinsRef.current = next;
        setPins(next);
      }),
    []
  );
  const settledOf = (pinId) => pinsRef.current.find((p) => p.id === pinId)?.settled || null;

  // End of a run: free the one-at-a-time lock — unless removePin / the unmount
  // took it first, and the drain may since have started the next pin on it.
  // Clearing it then would let a second pin run alongside that one.
  const releaseLock = useCallback((controller) => {
    if (abortRef.current?.controller !== controller) return;
    abortRef.current = null;
    runningRef.current = false;
  }, []);

  // Fresh staged pins (new id + num) for batch ops — all of them, even past 8
  // staged (applyStaged sends 8 at a time). An op that failed carries its error
  // so its box turns red and its popup shows why.
  const freshStagedPins = useCallback(
    (ops) =>
      ops.map((op) => {
        const num = numRef.current;
        numRef.current += 1;
        return opAsPin(op, {
          id: nextPinId(),
          num,
          error: op.status === "failed" ? op.error || "İşlem başarısız" : null,
        });
      }),
    []
  );

  // Swap a batch pin for its ops as stand-alone pins, in place and in order.
  // `opErrors`: a 400's per-op errors (see batchOpErrors).
  const unpackBatchPin = useCallback((pin, status, error = null, opErrors = null) => {
    const batchOps = pin.batchOps || [];
    const errors = batchOpErrors(batchOps, error, opErrors);
    const ops = batchOps.map((op, i) => opAsPin(op, { status, error: errors[i] }));
    setPins((prev) => prev.flatMap((p) => (p.id === pin.id ? ops : [p])));
  }, []);

  // Execute a batch pin: ONE job for all its ops on the current version. Same
  // mutex / abort / finally shape as executePin.
  const executeBatchPin = useCallback(
    async (pin) => {
      // Tepsiden reddedilmiş iş: sonucu bekleme/uygulama, op'ları geri ver.
      if (pin.settled === SETTLED_DECLINED) {
        unpackBatchPin(pin, "staged", DECLINED_ERROR);
        return;
      }
      // Batch path gone (a 404 earlier this session) before anything
      // was submitted: fall back to the chain, one legacy job per op.
      if (!pin.jobId && !isAreaOpsBatchEnabled()) {
        unpackBatchPin(pin, "queued");
        return;
      }
      runningRef.current = true;
      const controller = new AbortController();
      abortRef.current = { pinId: pin.id, controller };
      patchPin(pin.id, { status: "running", progress: 0, error: null });
      const onProgress = (progress) => patchPin(pin.id, { progress });

      // `pin` is the pre-submit snapshot — capture the id onJobId reports.
      let jobId = pin.jobId || null;
      try {
        const { url, partial, ops, steps } =
          pin.jobId && pin.status === "running"
            ? // Resume an in-flight batch from a previous session (no 2nd POST).
              await pollBatchJob(pin.jobId, { signal: controller.signal, onProgress })
            : await runAreaOpsBatch({
                ops: pin.batchOps,
                imageUrl: currentUrlRef.current || original,
                ctx,
                signal: controller.signal,
                onJobId: (id) => {
                  jobId = id;
                  patchPin(pin.id, { jobId: id });
                },
                onProgress,
              });

        // Show it only once the browser has it (see preloadImage).
        await preloadImage(url);
        if (controller.signal.aborted) return;

        const settled = settledOf(pin.id);
        if (settled === SETTLED_DECLINED) {
          unpackBatchPin(pin, "staged", DECLINED_ERROR);
          return;
        }
        setCurrentUrl(url);
        // Tepsi onayı sonucu kutuya zaten uyguladı ve işi onayladı → tekrarlama.
        if (settled !== SETTLED_APPROVED) {
          onApplyRef.current?.(boxId, url, null);
          // Applied to the canvas → close the job's "Onay Bekliyor" card in the widget.
          approveJob(jobId);
        }
        const batchOps = mergeBatchOpStatuses(pin.batchOps, ops, partial);
        // Failed ops come back as staged boxes, so "Tümünü Uygula" retries only them.
        const retry = freshStagedPins(batchOps.filter((op) => op.status === "failed"));
        // steps: the intermediate versions (sequential batch), listed before it.
        patchPin(pin.id, {
          status: "done",
          progress: 100,
          resultUrl: url,
          partial,
          batchOps,
          steps: steps || [],
        });
        if (retry.length) setPins((prev) => [...prev, ...retry]);
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (err?.code === "BATCH_UNSUPPORTED") {
          setBatchAvailable(false);
          unpackBatchPin(pin, "queued");
        } else if (err?.code === "INSUFFICIENT_BALANCE") {
          unpackBatchPin(pin, "staged");
          setBalanceBounce(err.data || {});
        } else {
          unpackBatchPin(pin, "staged", err?.message || "İşlem başarısız", err?.data?.errors);
        }
      } finally {
        releaseLock(controller);
        setPins((prev) => prev.slice());
      }
    },
    [boxId, ctx, original, patchPin, freshStagedPins, unpackBatchPin, releaseLock]
  );

  // Execute one pin (submit+poll, or resume-poll if it already has a jobId from
  // a prior session). Advances the history + applies the result on success.
  const executePin = useCallback(
    async (pin) => {
      if (pin.featureId === AREA_OPS_BATCH_OPERATION) {
        await executeBatchPin(pin);
        return;
      }
      if (pin.settled === SETTLED_DECLINED) {
        patchPin(pin.id, { status: "failed", error: DECLINED_ERROR });
        return;
      }
      const feature = getAreaOpFeature(pin.featureId);
      if (!feature) {
        patchPin(pin.id, { status: "failed", error: "Unknown feature" });
        return;
      }
      runningRef.current = true;
      const controller = new AbortController();
      abortRef.current = { pinId: pin.id, controller };
      patchPin(pin.id, { status: "running", progress: 0, error: null });

      const inputUrl = currentUrlRef.current || original;
      let jobId = pin.jobId || null;

      try {
        let resultUrl;
        // For "apply logo to all images", the job also returns a urlMap so the
        // canvas can swap EVERY affected image, not just this box's.
        let bulkUrlMap = null;
        if (pin.jobId && pin.status === "running") {
          // Resume an in-flight job from a previous session.
          resultUrl = await pollAreaOpJob(pin.jobId, {
            signal: controller.signal,
            onProgress: (progress) => patchPin(pin.id, { progress }),
          });
        } else {
          const out = await runAreaOp({
            feature,
            imageUrl: inputUrl,
            region: pin.region || null,
            config: pin.config || {},
            whole: !!pin.whole,
            ctx,
            signal: controller.signal,
            onProgress: (progress) => patchPin(pin.id, { progress }),
            onJobId: (id) => {
              jobId = id;
              patchPin(pin.id, { jobId: id });
            },
          });
          resultUrl = out.resultUrl;
          bulkUrlMap = out.urlMap || null;
        }

        // Show it only once the browser has it (see preloadImage).
        await preloadImage(resultUrl);
        if (controller.signal.aborted) return;

        // Advance the chain: this result becomes the current version + a new
        // entry in the history list (the done pin), applied to the box. When a
        // bulk urlMap is present, onApply also swaps every other affected image.
        const settled = settledOf(pin.id);
        if (settled === SETTLED_DECLINED) {
          patchPin(pin.id, { status: "failed", error: DECLINED_ERROR });
          return;
        }
        setCurrentUrl(resultUrl);
        // Tepsi onayı sonucu kutuya zaten uyguladı ve işi onayladı → tekrarlama.
        if (settled !== SETTLED_APPROVED) {
          onApplyRef.current?.(boxId, resultUrl, bulkUrlMap);
          // Applied to the canvas → close the job's "Onay Bekliyor" card in the widget.
          approveJob(jobId);
        }
        patchPin(pin.id, { status: "done", progress: 100, resultUrl, error: null });
      } catch (err) {
        if (err?.name === "AbortError") {
          // Pin was removed / reverted mid-run — leave state to the caller.
          return;
        }
        patchPin(pin.id, {
          status: "failed",
          error: err?.message || "İşlem başarısız",
        });
      } finally {
        releaseLock(controller);
        // Nudge the queue: a state update above re-runs the drain effect, but
        // guard against the rare all-synchronous path.
        setPins((prev) => prev.slice());
      }
    },
    [boxId, ctx, original, patchPin, executeBatchPin, releaseLock]
  );

  // Drain effect: whenever pins change and nothing is running, start the next
  // queued (or resumable running-with-jobId) pin.
  useEffect(() => {
    if (runningRef.current) return;
    const next = pins.find((p) => p.status === "queued" || (p.status === "running" && p.jobId));
    if (next) executePin(next);
  }, [pins, executePin]);

  // Abort an in-flight poll when the panel unmounts (closed / remounted). The
  // job keeps running server-side and is resumed by its jobId from localStorage
  // next time — this just prevents a setState-after-unmount and a double poll.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.controller.abort();
        abortRef.current = null;
      }
      runningRef.current = false;
    };
  }, []);

  // ---- public API -------------------------------------------------------

  // Add a draft pin (area already chosen). Returns the new pin id.
  // `area.whole`: "Tüm görsele uygula" — no region, the op runs on the whole
  // image. A whole-only feature (kind "whole") is always whole.
  const addDraft = useCallback((featureId, area = {}) => {
    const id = nextPinId();
    const num = numRef.current;
    numRef.current += 1;
    const feature = getAreaOpFeature(featureId);
    const whole = !!area.whole || feature?.kind === "whole";
    setPins((prev) => [
      ...prev,
      {
        id,
        num,
        featureId,
        status: "draft",
        region: whole ? null : area.region || null,
        point: whole ? null : area.point || null,
        whole,
        config: { ...(feature?.defaultConfig || {}) },
        jobId: null,
        resultUrl: null,
        error: null,
        progress: 0,
      },
    ]);
    return id;
  }, []);

  const updateDraftConfig = useCallback((id, patch) => {
    setPins((prev) =>
      prev.map((p) => (p.id === id ? { ...p, config: { ...p.config, ...patch } } : p))
    );
  }, []);

  // Resize a draft's box (edge / corner handles on the image).
  const updateDraftRegion = useCallback((id, region) => {
    setPins((prev) =>
      prev.map((p) => (p.id === id && p.status === "draft" ? { ...p, region } : p))
    );
  }, []);

  // Move a draft into the run queue.
  const applyPin = useCallback((id) => {
    setPins((prev) =>
      prev.map((p) => (p.id === id && p.status === "draft" ? { ...p, status: "queued" } : p))
    );
  }, []);

  // "Listeye Ekle": park a ready draft as staged (max 8). Nothing runs until
  // applyStaged — the list is the only way an op runs. Any ready op is
  // accepted; applyStaged decides which ones the batch can carry.
  const stagePin = useCallback((id) => {
    setPins((prev) => {
      const pin = prev.find((p) => p.id === id);
      if (!pin || pin.status !== "draft" || countStaged(prev) >= MAX_BATCH_OPS) return prev;
      if (!isAreaOpReady(getAreaOpFeature(pin.featureId), pin.config, { whole: !!pin.whole })) {
        return prev;
      }
      return prev.map((p) => (p.id === id ? { ...p, status: "staged", error: null } : p));
    });
  }, []);

  // Back to draft for editing (click on its box). The pin keeps its array
  // position, so it keeps its place in the batch order when staged again.
  const unstagePin = useCallback((id) => {
    setPins((prev) =>
      prev.map((p) => (p.id === id && p.status === "staged" ? { ...p, status: "draft" } : p))
    );
  }, []);

  // "Uygula" under the photo. 2+ ops the batch can carry → one batch pin of
  // the first 8 (ops a batch handed back can push the list past 8; those wait
  // for the next click). Every other staged op — the only batchable one, a
  // whole-image step outside STAGEABLE_WHOLE_FEATURE_IDS, add-logo on every
  // image, or all of them with no batch path — runs on its own (legacy chain),
  // AFTER the batch: its regions were drawn on the current image. Yeniden
  // Boyutlandır goes last of all, since a new ratio would shift every region.
  // The batch pin is built out here, not in the setPins updater: StrictMode
  // double-invokes updaters, which would mint two ids / nums.
  const applyStaged = useCallback(() => {
    const allStaged = pinsRef.current.filter((p) => p.status === "staged");
    if (allStaged.length === 0) return;
    const batchable = batchAvailable
      ? allStaged.filter((p) =>
          canStageAreaOp(getAreaOpFeature(p.featureId), p.config, { whole: !!p.whole })
        )
      : [];
    const inBatch = batchable.length >= 2 ? batchable : [];
    const members = inBatch.slice(0, MAX_BATCH_OPS);
    const memberIds = new Set(members.map((p) => p.id));
    const waitingIds = new Set(inBatch.slice(MAX_BATCH_OPS).map((p) => p.id));
    let batchPin = null;
    if (members.length) {
      const num = numRef.current;
      numRef.current += 1;
      batchPin = {
        id: nextPinId(),
        num,
        featureId: AREA_OPS_BATCH_OPERATION,
        status: "queued",
        region: null,
        point: null,
        config: {},
        batchOps: members.map((p) => ({
          opId: p.id,
          featureId: p.featureId,
          region: p.region,
          whole: !!p.whole,
          config: p.config || {},
          num: p.num,
          status: "pending",
          error: null,
        })),
        partial: false,
        jobId: null,
        resultUrl: null,
        error: null,
        progress: 0,
      };
    }
    setPins((prev) => {
      const isSolo = (p) => p.status === "staged" && !memberIds.has(p.id) && !waitingIds.has(p.id);
      const solos = prev.filter(isSolo).map((p) => ({ ...p, status: "queued" }));
      return [
        ...prev.filter((p) => !memberIds.has(p.id) && !isSolo(p)),
        ...(batchPin ? [batchPin] : []),
        ...solos.filter((p) => p.featureId !== "resize"),
        ...solos.filter((p) => p.featureId === "resize"),
      ];
    });
  }, [batchAvailable]);

  // Remove a pin (e.g. "Vazgeç" on a draft popup). Aborts it if it's running.
  const removePin = useCallback((id) => {
    if (abortRef.current?.pinId === id) {
      abortRef.current.controller.abort();
      abortRef.current = null;
      runningRef.current = false;
    }
    setPins((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // Jump to the version produced by a (done) pin — NON-destructive: just move
  // the current pointer + re-apply that image. Every other version stays in the
  // list so you can go back and forth freely.
  const revertToPin = useCallback(
    (pinId) => {
      const pin = pinsRef.current.find((p) => p.id === pinId);
      if (!pin || !pin.resultUrl) return;
      setCurrentUrl(pin.resultUrl);
      onApplyRef.current?.(boxId, pin.resultUrl);
    },
    [boxId]
  );

  // Jump to any version by its image — a sequential batch's intermediate
  // steps have no pin of their own. NON-destructive, like revertToPin.
  const revertToVersion = useCallback(
    (url) => {
      if (!url) return;
      setCurrentUrl(url);
      onApplyRef.current?.(boxId, url);
    },
    [boxId]
  );

  // Jump back to the original image — also NON-destructive (versions kept).
  const revertToOriginal = useCallback(() => {
    setCurrentUrl(original);
    onApplyRef.current?.(boxId, original);
  }, [boxId, original]);

  // Drop a persisted queue entirely (e.g. box deleted). Not wired to UI yet.
  const clearStorage = useCallback(() => {
    if (boxId) localStorage.removeItem(storageKey(boxId));
  }, [boxId]);

  const busy = pins.some((p) => p.status === "running" || p.status === "queued");
  const stagedPins = useMemo(() => pins.filter((p) => p.status === "staged"), [pins]);

  return {
    pins,
    currentUrl,
    originalUrl: original,
    busy,
    addDraft,
    updateDraftConfig,
    updateDraftRegion,
    applyPin,
    removePin,
    revertToPin,
    revertToVersion,
    revertToOriginal,
    clearStorage,
    stagedPins,
    batchAvailable,
    stagePin,
    unstagePin,
    applyStaged,
  };
}
